import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { Subscription, BehaviorSubject } from 'rxjs';
import { ClipboardService } from 'ngx-clipboard';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../../services/user.service';
import { EmployeeService } from '../../../services/employee.service';
import { CompanyService } from '../../../services/company.service';
import { EmployeeEditModalComponent } from '../employee-edit-modal/employee-edit-modal.component';
import { EmployeeData } from '../../../interfaces';
import { ACTION, EMPLOYEE_ROLES, SUBJECT } from '../../../enums';
import { PermissionService } from '../../../services/permission.service';

@Component({
  selector: 'app-employee-view-modal',
  templateUrl: './employee-view-modal.component.html',
  styleUrls: ['./employee-view-modal.component.scss']
})
export class EmployeeViewModalComponent implements OnInit, OnDestroy {

  protected readonly ACTION = ACTION;
  protected readonly SUBJECT = SUBJECT;

  private locationsListSub?: Subscription;

  protected readonly EMPLOYEE_ROLES = EMPLOYEE_ROLES;

  data$ = new BehaviorSubject<EmployeeData | null>(null);
  locationName: string | undefined;
  isOwnProfile: boolean = false;

  constructor(
    public employeeService: EmployeeService,
    public companyService: CompanyService,
    public userService: UserService,
    protected readonly permissionService: PermissionService,
    private readonly dialogService: MatDialog,
    private readonly toastrService: ToastrService,
    private readonly clipboardService: ClipboardService,
    private readonly translate: TranslateService,
    public dialogRef: MatDialogRef<EmployeeViewModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public employeeId: number
  ) {}

  ngOnInit(): void {
    this.isOwnProfile = this.employeeId == this.userService.profileInfo$.getValue().id;

    if (!this.companyService.isEmployeesUpToDate$.getValue()) {
      this.employeeService.updateEmployeesList();
    }

    if (this.employeeService.locationsList$.getValue().length == 0) {
      this.employeeService.updateLocationsList();
    }

    this.employeeService.getEmployeeByIdRequest(this.employeeId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.data$.next(response.data);

          this.locationsListSub = this.employeeService.locationsList$.subscribe((locations) => {
            this.locationName = locations.find((loc) => loc.id === response.data.location)?.displayName;
          });
        }
      }
    });
  }

  timezoneOffset(localTime: string): string {
    if (!localTime) return '';

    const match = localTime.match(/([+-]\d{2}):(\d{2})$/);
    return match ? `(GMT ${match[1]}:${match[2]})` : '(GMT 00:00)';
  }

  isFieldNotEmpty(fieldName: keyof EmployeeData): boolean {
    const data = this.data$.getValue();
    const value = data?.[fieldName];

    return value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0);
  }

  onCopyText(copyText: string) {
    this.clipboardService.copy(copyText);
    this.toastrService.success(this.translate.instant('messages.copied_clipboard'));
  }

  onEdit() {
    this.dialogRef.close();

    this.dialogService.open(EmployeeEditModalComponent, {
      panelClass: 'dialog-overlay-pane',
      data: this.data$.getValue(),
      width: '920px'
    });
  }

  ngOnDestroy(): void {
    this.locationsListSub?.unsubscribe();
  }

}
